import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import {
  Box,
  TextField,
  Autocomplete,
  MenuItem,
  Button,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Snackbar,
  Card,
  CardContent,
  Avatar,
  Tabs,
  Tab,
} from '@mui/material';
import dayjs from 'dayjs';
import Header from '../../../components/Header';
import Appfooter from '../../../components/Appfooter';
import Load from '../../../components/Load';
import {
  TORNEIO_POR_ID,
  TorneioPorIdData,
  TorneioPorIdVars,
} from '../../../services/torneioService';
import {
  buscarInscricaoPorId,
  criarInscricaoTorneio,
  atualizarInscricaoTorneio,
  deletarInscricaoTorneio,
  CriarInscricaoTorneioInput,
  AtualizarInscricaoTorneioInput,
} from '../../../services/inscricaoTorneioService';
import { buscarAtletasPorUsername, buscarAtletaPorId, Atleta } from '../../../services/atletaService';
import { criarUsuarioEAtletaOpcional } from '../../../services/userService';
import {
  pagamentoPorId,
  atualizarPagamentoInscricao,
  deletarPagamentoInscricao,
} from '../../../services/pagamentoInscricaoService';
import type { PagamentoInscricao, AtualizarPagamentoInscricaoInput } from '../../../services/pagamentoInscricaoService';
import type { StatusPagamento } from '../../../types/pagamento';
import {
  buscarCategoriaTorneioPorId,
  CategoriaTorneio,
} from '../../../services/categoriaTorneioService';
import { useAuth } from '../../../context/AuthContext';
import { canManageInscricoes } from '../../../utils/torneioPermissions';
import { dispatchGlobalError } from '../../../context/ApiErrorContext';
import { validateCpf } from '../../../utils/cpf';
import { validateEmail } from '../../../utils/email';
import { StatusInscricao } from '../../../types/inscricao';
import { PageBreadcrumb } from '../../../components/navigation';

interface AtletaOption {
  id: string;
  nome: string;
  email: string;
  username: string;
  imagemUrl?: string | null;
  telefone?: string | null;
  tamanhoCamiseta?: string | null;
  tamanhoSapato?: number | null;
}

interface CategoriaOption {
  id: string;
  nome: string;
}

const getStatusOptions = (isEditMode: boolean) => {
  const baseOptions = [
    { label: 'Pendente', value: StatusInscricao.PENDENTE },
    { label: 'Confirmada', value: StatusInscricao.CONFIRMADA },
    { label: 'Fila de Espera', value: StatusInscricao.FILA_ESPERA },
  ];
  
  if (isEditMode) {
    baseOptions.push({ label: 'Cancelada', value: StatusInscricao.CANCELADA });
  }
  
  return baseOptions;
};

const EditInscricaoPage: React.FC = () => {
  const { torneioId, inscricaoId, categoriaId } = useParams<{
    torneioId: string;
    inscricaoId?: string;
    categoriaId?: string;
  }>();
  const [searchParams] = useSearchParams();
  const categoriaIdFromQuery = searchParams.get('categoriaId');
  const tabFromQuery = searchParams.get('tab');
  const navigate = useNavigate();
  const auth = useAuth();
  const user = (auth?.user as any) || (typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('user') || 'null') : null);

  const isEditMode = !!inscricaoId;
  const resolvedCategoriaId = categoriaId || categoriaIdFromQuery;

  // Estados
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [categoria, setCategoria] = useState<CategoriaTorneio | null>(null);
  const [categorias, setCategorias] = useState<CategoriaOption[]>([]);
  const [atletasSelecionados, setAtletasSelecionados] = useState<(AtletaOption | undefined)[]>([]);
  const [status, setStatus] = useState<string>(StatusInscricao.PENDENTE);
  const [observacao, setObservacao] = useState<string>('');
  const [dataInscricao, setDataInscricao] = useState<string>(
    dayjs().format('YYYY-MM-DDTHH:mm')
  );

  // Estados para criação de novo atleta
  const [showCreateAtletaModal, setShowCreateAtletaModal] = useState(false);
  const [createAtletaIndex, setCreateAtletaIndex] = useState<number | null>(null);
  const [novoAtletaNome, setNovoAtletaNome] = useState('');
  const [novoAtletaEmail, setNovoAtletaEmail] = useState('');
  const [novoAtletaCpf, setNovoAtletaCpf] = useState('');
  const [novoAtletaTelefone, setNovoAtletaTelefone] = useState('');
  const [novoAtletaDataNascimento, setNovoAtletaDataNascimento] = useState('');
  const [novoAtletaCidadeId, setNovoAtletaCidadeId] = useState('');
  const [creatingAtleta, setCreatingAtleta] = useState(false);

  // Estados para busca de atletas
  const [atletaSearchInputs, setAtletaSearchInputs] = useState<string[]>(['']);
  const [atletaOptions, setAtletaOptions] = useState<AtletaOption[][]>([]);
  const [loadingAtletas, setLoadingAtletas] = useState<boolean[]>([]);

  // Estado para Snackbar de sucesso
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  // Estados para abas e pagamento
  const [tabValue, setTabValue] = useState(() => {
    const t = parseInt(tabFromQuery || '0', 10);
    return isNaN(t) ? 0 : t;
  });
  const [pagamento, setPagamento] = useState<PagamentoInscricao | null>(null);
  const [pagamentoStatus, setPagamentoStatus] = useState<string>('');
  const [pagamentoDataVencimento, setPagamentoDataVencimento] = useState<string>('');
  const [pagamentoMotivoCancelamento, setPagamentoMotivoCancelamento] = useState<string>('');
  const [savingPagamento, setSavingPagamento] = useState(false);

  // Estados para dialog de confirmação de delete
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteBlockedDialogOpen, setDeleteBlockedDialogOpen] = useState(false);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Query para buscar torneio
  const { data: torneioData, loading: loadingTorneio } = useQuery<
    TorneioPorIdData,
    TorneioPorIdVars
  >(TORNEIO_POR_ID, {
    variables: { id: torneioId! },
    skip: !torneioId,
    fetchPolicy: 'no-cache',
  });

  const torneio = torneioData?.torneioPorId;

  // Verificar permissões
  const hasPermission = useMemo(() => {
    if (!torneio || !user) return false;
    return canManageInscricoes(torneio, user.id, user.role);
  }, [torneio, user]);

  // Funções helper para Snackbar
  const showSuccess = (message: string) => {
    setSnackbar({ open: true, message, severity: 'success' });
  };

  const showError = (message: string) => {
    setSnackbar({ open: true, message, severity: 'error' });
    dispatchGlobalError(message);
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Carregar dados iniciais
  useEffect(() => {
    const loadData = async () => {
      if (!torneioId) {
        setError('Torneio não encontrado');
        setLoading(false);
        return;
      }

      try {
        // Carregar categorias do torneio
        if (torneio?.categoriasTorneio) {
          setCategorias(
            torneio.categoriasTorneio.map((cat) => ({
              id: cat.id,
              nome: cat.nome,
            }))
          );
        }

        // Se estiver em modo edição, carregar inscrição
        if (isEditMode && inscricaoId) {
          const inscricao = await buscarInscricaoPorId(inscricaoId);
          if (!inscricao) {
            setError('Inscrição não encontrada');
            setLoading(false);
            return;
          }

          // Carregar categoria
          const cat = await buscarCategoriaTorneioPorId(inscricao.categoriaTorneio.id);
          setCategoria(cat);

          // Preencher dados da inscrição
          setStatus(inscricao.status);
          setObservacao(inscricao.observacao || '');
          if (inscricao.dataInscricao) {
            setDataInscricao(dayjs(inscricao.dataInscricao).format('YYYY-MM-DDTHH:mm'));
          }

          // Preencher atletas
          const atletasBase = inscricao.equipe.atletas.map((atleta) => ({
            id: atleta.id,
            nome: atleta.usuario?.nome ?? 'Atleta',
            email: atleta.usuario?.email ?? '',
            username: atleta.usuario?.username || '',
            imagemUrl: atleta.usuario?.imagemUrl ?? null,
          }));

          // Buscar detalhes (telefone, camiseta/sapato)
          const atletasComDetalhes = await Promise.all(
            atletasBase.map(async (a) => {
              try {
                const detalhe = await buscarAtletaPorId(a.id);
                return {
                  ...a,
                  telefone: detalhe?.telefone ?? null,
                  tamanhoCamiseta: detalhe?.tamanhoCamiseta ?? null,
                  tamanhoSapato: detalhe?.tamanhoSapato ?? null,
                };
              } catch {
                return a;
              }
            })
          );
          setAtletasSelecionados(atletasComDetalhes);
          setAtletaSearchInputs(new Array(atletasBase.length).fill(''));
          setAtletaOptions(new Array(atletasBase.length).fill([]));
          setLoadingAtletas(new Array(atletasBase.length).fill(false));

          // Carregar pagamento vinculado
          if (inscricao.pagamentoInscricao?.id) {
            try {
              const pag = await pagamentoPorId(inscricao.pagamentoInscricao.id);
              setPagamento(pag);
              setPagamentoStatus(pag.status || '');
              setPagamentoDataVencimento(
                pag.dataVencimento ? dayjs(pag.dataVencimento).format('YYYY-MM-DDTHH:mm') : ''
              );
              setPagamentoMotivoCancelamento(pag.motivoCancelamento || '');
            } catch {
              // Pagamento não encontrado ou erro — ignorar
            }
          }
        } else if (resolvedCategoriaId) {
          // Modo criação com categoria pré-selecionada
          const cat = await buscarCategoriaTorneioPorId(resolvedCategoriaId);
          setCategoria(cat);
          const numeroAtletas = cat.numeroAtletas || 1;
          setAtletaSearchInputs(new Array(numeroAtletas).fill(''));
          setAtletaOptions(new Array(numeroAtletas).fill([]));
          setLoadingAtletas(new Array(numeroAtletas).fill(false));
        } else {
          // Modo criação sem categoria pré-selecionada
          setAtletaSearchInputs(['']);
          setAtletaOptions([[]]);
          setLoadingAtletas([false]);
        }
      } catch (err: any) {
        const errorMsg = err.message || 'Erro ao carregar dados';
        setError(errorMsg);
        showError(errorMsg);
      } finally {
        setLoading(false);
      }
    };

    if (torneio) {
      loadData();
    }
  }, [torneioId, inscricaoId, isEditMode, resolvedCategoriaId, torneio]);

  // Atualizar número de campos de atleta quando categoria mudar
  useEffect(() => {
    if (categoria && !isEditMode) {
      const numeroAtletas = categoria.numeroAtletas || 1;
      setAtletaSearchInputs(new Array(numeroAtletas).fill(''));
      setAtletaOptions(new Array(numeroAtletas).fill([]));
      setLoadingAtletas(new Array(numeroAtletas).fill(false));
      setAtletasSelecionados([]);
    }
  }, [categoria, isEditMode]);

  // Buscar atletas quando input mudar
  useEffect(() => {
    const searchTimers: NodeJS.Timeout[] = [];

    atletaSearchInputs.forEach((input, index) => {
      if (input.trim().length >= 2) {
        const timer = setTimeout(async () => {
          setLoadingAtletas((prev) => {
            const newArr = [...prev];
            newArr[index] = true;
            return newArr;
          });

          try {
            const results = await buscarAtletasPorUsername(input, resolvedCategoriaId ?? undefined);
            const options: AtletaOption[] = results.map((atleta) => ({
              id: atleta.id,
              nome: atleta.usuario?.nome || '',
              email: atleta.usuario?.email || '',
              username: atleta.usuario?.username || '',
              imagemUrl: atleta.usuario?.imagemUrl || null,
            }));

            setAtletaOptions((prev) => {
              const newArr = [...prev];
              newArr[index] = options;
              return newArr;
            });
          } catch (err) {
            console.error('Erro ao buscar atletas:', err);
          } finally {
            setLoadingAtletas((prev) => {
              const newArr = [...prev];
              newArr[index] = false;
              return newArr;
            });
          }
        }, 300);

        searchTimers.push(timer);
      } else {
        setAtletaOptions((prev) => {
          const newArr = [...prev];
          newArr[index] = [];
          return newArr;
        });
      }
    });

    return () => {
      searchTimers.forEach((timer) => clearTimeout(timer));
    };
  }, [atletaSearchInputs]);

  const handleCategoriaChange = async (categoriaId: string) => {
    try {
      const cat = await buscarCategoriaTorneioPorId(categoriaId);
      setCategoria(cat);
    } catch (err: any) {
      showError(err.message || 'Erro ao carregar categoria');
    }
  };

  const handleAtletaSelect = async (index: number, atleta: AtletaOption | null) => {
    if (atleta) {
      let enrichedAtleta = atleta;
      if (torneio?.camisetas || torneio?.sapatos) {
        try {
          const detalhe = await buscarAtletaPorId(atleta.id);
          enrichedAtleta = {
            ...atleta,
            tamanhoCamiseta: detalhe?.tamanhoCamiseta ?? null,
            tamanhoSapato: detalhe?.tamanhoSapato ?? null,
          };
        } catch {
          // Manter atleta sem dados de tamanho em caso de erro
        }
      }
      setAtletasSelecionados((prev) => {
        // Garantir que o array tenha o tamanho correto
        const expectedLength = categoria?.numeroAtletas || atletaSearchInputs.length;
        const newArr: (AtletaOption | undefined)[] = [...prev];
        // Preencher com undefined se necessário para atingir o tamanho esperado
        while (newArr.length < expectedLength) {
          newArr.push(undefined);
        }
        newArr[index] = enrichedAtleta;
        return newArr;
      });
      setAtletaSearchInputs((prev) => {
        const newArr = [...prev];
        newArr[index] = atleta.nome;
        return newArr;
      });
    } else {
      // Se null, limpar a seleção
      setAtletasSelecionados((prev) => {
        const newArr: (AtletaOption | undefined)[] = [...prev];
        newArr[index] = undefined;
        return newArr;
      });
    }
  };

  const handleOpenCreateAtletaModal = (index: number) => {
    setCreateAtletaIndex(index);
    setShowCreateAtletaModal(true);
    setNovoAtletaNome('');
    setNovoAtletaEmail('');
    setNovoAtletaCpf('');
    setNovoAtletaTelefone('');
    setNovoAtletaDataNascimento('');
    setNovoAtletaCidadeId('');
  };

  const handleCreateAtleta = async () => {
    if (!novoAtletaNome.trim() || !novoAtletaEmail.trim()) {
      showError('Nome e email são obrigatórios');
      return;
    }

    // Validação de email
    if (!validateEmail(novoAtletaEmail)) {
      showError('Email inválido');
      return;
    }

    if (novoAtletaCpf && !validateCpf(novoAtletaCpf)) {
      showError('CPF inválido');
      return;
    }

    setCreatingAtleta(true);
    try {
      const result = await criarUsuarioEAtletaOpcional({
        name: novoAtletaNome,
        email: novoAtletaEmail,
        password: undefined, // Backend deve gerar senha temporária
        cpf: novoAtletaCpf || undefined,
        telefone: novoAtletaTelefone || undefined,
        dataNascimento: novoAtletaDataNascimento || undefined,
        cidadeId: novoAtletaCidadeId || undefined,
      });

      if (result && createAtletaIndex !== null) {
        // Usar os dados retornados pela mutation diretamente
        if (result.id && result.usuario) {
          const atletaOption: AtletaOption = {
            id: result.id,
            nome: result.usuario.nome,
            email: result.usuario.email,
            username: result.usuario.username ?? result.usuario.email ?? '',
            imagemUrl: result.usuario.imagemUrl || null,
          };
          handleAtletaSelect(createAtletaIndex, atletaOption);
          showSuccess('Atleta criado com sucesso');
        } else if (result.id) {
          // Fallback: buscar atleta por id se a mutation não retornar o usuario completo
          const novoAtleta = await buscarAtletaPorId(result.id);
          if (novoAtleta?.usuario) {
            const atletaOption: AtletaOption = {
              id: novoAtleta.id,
              nome: novoAtleta.usuario.nome,
              email: novoAtleta.usuario.email,
              username: novoAtleta.usuario.username ?? novoAtleta.usuario.email ?? '',
              imagemUrl: novoAtleta.usuario.imagemUrl || null,
            };
            handleAtletaSelect(createAtletaIndex, atletaOption);
            showSuccess('Atleta criado com sucesso');
          } else {
            showError('Atleta criado mas não foi possível selecioná-lo automaticamente');
          }
        } else {
          // Fallback: buscar por username/email se tivermos usuario mas não id
          const searchTerm = result.usuario?.username ?? result.usuario?.email ?? '';
          const atletas = searchTerm ? await buscarAtletasPorUsername(searchTerm) : [];
          const novoAtleta = atletas.find((a) => a.usuario?.email === novoAtletaEmail);

          if (novoAtleta) {
            const atletaOption: AtletaOption = {
              id: novoAtleta.id,
              nome: novoAtleta.usuario?.nome || novoAtletaNome,
              email: novoAtleta.usuario?.email || novoAtletaEmail,
              username: novoAtleta.usuario?.username ?? novoAtleta.usuario?.email ?? '',
              imagemUrl: novoAtleta.usuario?.imagemUrl || null,
            };
            handleAtletaSelect(createAtletaIndex, atletaOption);
            showSuccess('Atleta criado com sucesso');
          } else {
            showError('Atleta criado mas não foi possível selecioná-lo automaticamente');
          }
        }
      }

      setShowCreateAtletaModal(false);
    } catch (err: any) {
      showError(err.message || 'Erro ao criar atleta');
    } finally {
      setCreatingAtleta(false);
    }
  };

  const validarFormulario = (): boolean => {
    if (!categoria) {
      showError('Selecione uma categoria');
      return false;
    }

    const numeroEsperado = categoria.numeroAtletas || 1;
    // Filtrar apenas atletas válidos (não nulos/undefined e com ID)
    const atletasValidos: AtletaOption[] = atletasSelecionados.filter(
      (a): a is AtletaOption => a !== undefined && a !== null && typeof a === 'object' && 'id' in a && !!a.id
    );
    
    if (atletasValidos.length !== numeroEsperado) {
      showError(`Selecione ${numeroEsperado} atleta(s)`);
      return false;
    }

    // Verificar se não há atletas duplicados
    const idsUnicos = new Set(atletasValidos.map((a) => a.id));
    if (idsUnicos.size !== atletasValidos.length) {
      showError('Não é possível selecionar o mesmo atleta mais de uma vez');
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!validarFormulario() || !categoria) return;

    // Filtrar apenas atletas válidos
    const atletasValidos: AtletaOption[] = atletasSelecionados.filter(
      (a): a is AtletaOption => a !== undefined && a !== null && typeof a === 'object' && 'id' in a && !!a.id
    );

    setSaving(true);
    try {
      if (isEditMode && inscricaoId) {
        // Atualizar inscrição
        const input: AtualizarInscricaoTorneioInput = {
          categoriaTorneioId: categoria.id,
          status,
          observacao: observacao.trim() || undefined,
          dataInscricao: dataInscricao ? dayjs(dataInscricao).format('YYYY-MM-DD') : undefined,
          equipe: {
            atletas: atletasValidos.map((a) => ({ id: a.id })),
            nome: '',
            cidade: '',
          },
        };

        await atualizarInscricaoTorneio(inscricaoId, input);
        showSuccess('Inscrição atualizada com sucesso');
        
        // Navegar de volta após um breve delay para mostrar a mensagem
        setTimeout(() => {
          // Se veio de uma página específica, voltar para ela; caso contrário, voltar para o torneio
          if (resolvedCategoriaId) {
            navigate(`/liga/tournament/${torneioId}/category/${resolvedCategoriaId}/manage?tab=1`);
          } else {
            navigate(`/liga/tournament/${torneioId}?tab=1`);
          }
        }, 1000);
      } else {
        // Criar inscrição
        const input: CriarInscricaoTorneioInput = {
          categoriaTorneioId: categoria.id,
          status,
          observacao: observacao.trim() || undefined,
          dataInscricao: dataInscricao ? dayjs(dataInscricao).format('YYYY-MM-DD') : undefined,
          equipe: {
            nome: '',
            cidade: '',
            atletas: atletasValidos.map((a) => ({ id: a.id })),
          },
        };

        const res = await criarInscricaoTorneio(input);
        if (!res) throw new Error('Erro ao criar inscrição');

        showSuccess('Inscrição criada com sucesso');

        // Navegar de volta após um breve delay para mostrar a mensagem
        setTimeout(() => {
          if (resolvedCategoriaId) {
            navigate(`/liga/tournament/${torneioId}/category/${resolvedCategoriaId}/manage?tab=1`);
          } else {
            navigate(`/liga/tournament/${torneioId}?tab=1`);
          }
        }, 1000);
      }
    } catch (err: any) {
      showError(err.message || 'Erro ao salvar inscrição');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = () => {
    if (!inscricaoId) return;

    if (pagamento?.invoiceId) {
      setDeleteBlockedDialogOpen(true);
      return;
    }

    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!inscricaoId) return;

    setSaving(true);
    try {
      // Deletar pagamento primeiro, se existir
      if (pagamento?.id) {
        await deletarPagamentoInscricao(pagamento.id);
      }

      await deletarInscricaoTorneio(inscricaoId);
      setDeleteDialogOpen(false);
      showSuccess('Inscrição e pagamento deletados com sucesso');

      // Navegar de volta após um breve delay para mostrar a mensagem
      setTimeout(() => {
        if (resolvedCategoriaId) {
          navigate(`/liga/tournament/${torneioId}/category/${resolvedCategoriaId}/manage?tab=1`);
        } else {
          navigate(`/liga/tournament/${torneioId}?tab=1`);
        }
      }, 1000);
    } catch (err: any) {
      showError(err.message || 'Erro ao deletar inscrição');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePagamento = async () => {
    if (!pagamento?.id) return;
    setSavingPagamento(true);
    try {
      const input: AtualizarPagamentoInscricaoInput = {
        status: pagamentoStatus || null,
        dataVencimento: pagamentoDataVencimento ? dayjs(pagamentoDataVencimento).format('YYYY-MM-DD') : null,
        motivoCancelamento: pagamentoMotivoCancelamento || null,
      };
      const updated = await atualizarPagamentoInscricao(pagamento.id, input);
      setPagamento(updated);
      showSuccess('Pagamento atualizado com sucesso');
    } catch (err: any) {
      showError(err.message || 'Erro ao atualizar pagamento');
    } finally {
      setSavingPagamento(false);
    }
  };

  if (loading || loadingTorneio) {
    return (
      <>
        <Header />
        <div className="main-content right-chat-active">
          <div className="middle-sidebar-bottom">
            <div className="middle-sidebar-left">
              <div className="row feed-body">
                <div className="col-xl-12">
                  <Load />
                </div>
              </div>
            </div>
          </div>
        </div>
        <Appfooter />
      </>
    );
  }

  if (!torneio) {
    return (
      <>
        <Header />
        <div className="main-content right-chat-active">
          <div className="middle-sidebar-bottom">
            <div className="middle-sidebar-left">
              <div className="row feed-body">
                <div className="col-xl-12">
                  <Alert severity="error">Torneio não encontrado</Alert>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Appfooter />
      </>
    );
  }

  if (!hasPermission) {
    return (
      <>
        <Header />
        <div className="main-content right-chat-active">
          <div className="middle-sidebar-bottom">
            <div className="middle-sidebar-left">
              <div className="row feed-body">
                <div className="col-xl-12">
                  <Alert severity="warning">
                    Você não tem permissão para gerenciar inscrições deste torneio.
                  </Alert>
                  <Box mt={2}>
                    <PageBreadcrumb
                      items={[
                        { label: 'Dashboard', href: '/liga/dashboard' },
                        { label: 'Torneios', href: '/liga/tournaments' },
                        { label: torneio?.nome ?? 'Torneio' },
                      ]}
                      onBack={() => navigate(-1)}
                      variant="bootstrap"
                    />
                  </Box>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Appfooter />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="main-content right-chat-active">
        <div className="middle-sidebar-bottom">
          <div className="middle-sidebar-left">
            <div className="row feed-body">
              <div className="col-xl-12">
                <Paper className="card w-100 shadow-xss rounded-xxl border-0 mb-3 mt-3">
                  <div className="card-body p-4">
                    <Box mb={3}>
                      <PageBreadcrumb
                        items={[
                          { label: 'Dashboard', href: '/liga/dashboard' },
                          { label: 'Torneios', href: '/liga/tournaments' },
                          {
                            label: torneio?.nome ?? 'Torneio',
                            href: torneioId ? `/liga/tournament/${torneioId}` : undefined,
                          },
                          ...(categoria && resolvedCategoriaId
                            ? [
                                {
                                  label: categoria.nome,
                                  href: `/liga/tournament/${torneioId}/category/${resolvedCategoriaId}/manage`,
                                },
                              ]
                            : []),
                          {
                            label: isEditMode ? 'Editar Inscrição' : 'Nova Inscrição',
                          },
                        ]}
                        onBack={() => navigate(-1)}
                        variant="bootstrap"
                      />
                    </Box>
                    <Box mb={3}>
                      <Typography variant="h5" component="h1">
                        {isEditMode ? 'Editar Inscrição' : 'Nova Inscrição'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Torneio: {torneio.nome}
                      </Typography>
                    </Box>

                    {error && (
                      <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                      </Alert>
                    )}

                    {isEditMode && (
                      <Box mb={3}>
                        <Tabs
                          value={tabValue}
                          onChange={handleTabChange}
                          indicatorColor="primary"
                          textColor="primary"
                        >
                          <Tab label="Inscrição" />
                          <Tab label="Pagamento" />
                        </Tabs>
                      </Box>
                    )}

                    {/* === TAB 0: Inscrição (ou modo criação sem abas) === */}
                    {(!isEditMode || tabValue === 0) && (<>
                    {/* Seleção de Categoria (apenas modo criação) */}
                    {!isEditMode && (
                      <Box mb={3}>
                        <Autocomplete
                          options={categorias}
                          getOptionLabel={(option) => option.nome}
                          value={categoria ? { id: categoria.id, nome: categoria.nome } : null}
                          onChange={(_, newValue) => {
                            if (newValue) {
                              handleCategoriaChange(newValue.id);
                            }
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Categoria *"
                              required
                              disabled={saving}
                            />
                          )}
                          disabled={saving}
                        />
                        {categoria && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Número de atletas: {categoria.numeroAtletas || 1}
                          </Typography>
                        )}
                      </Box>
                    )}

                    {/* Exibir categoria no modo edição */}
                    {isEditMode && categoria && (
                      <Box mb={3}>
                        <TextField
                          label="Categoria"
                          value={categoria.nome}
                          disabled
                          fullWidth
                        />
                      </Box>
                    )}

                    {/* Seleção de Atleta(s) */}
                    {categoria && (
                      <Box mb={3}>
                        <Typography variant="h6" gutterBottom>
                          Atleta(s) *
                        </Typography>
                        <Box display="flex" gap={2} flexWrap="wrap">
                          {atletaSearchInputs.map((_, index) => (
                            <Box key={index} sx={{ flex: '1 1 calc(50% - 8px)', maxWidth: 'calc(50% - 8px)', minWidth: 280 }}>
                              <Card variant="outlined" sx={{ height: '100%' }}>
                                <CardContent>
                                  <Autocomplete
                                    options={atletaOptions[index] || []}
                                    getOptionLabel={(option) =>
                                      `${option.nome} (${option.username})`
                                    }
                                    value={atletasSelecionados[index] || null}
                                    onChange={(_, newValue) => {
                                      handleAtletaSelect(index, newValue);
                                    }}
                                    inputValue={atletaSearchInputs[index]}
                                    onInputChange={(_, newInputValue) => {
                                      setAtletaSearchInputs((prev) => {
                                        const newArr = [...prev];
                                        newArr[index] = newInputValue;
                                        return newArr;
                                      });
                                    }}
                                    loading={loadingAtletas[index]}
                                    noOptionsText={loadingAtletas[index] ? "Buscando..." : atletaSearchInputs[index].trim().length < 2 ? "Digite o username para buscar" : "Nenhum atleta encontrado"}
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        label={`Atleta ${index + 1} *`}
                                        required
                                        disabled={saving}
                                        InputProps={{
                                          ...params.InputProps,
                                          endAdornment: (
                                            <>
                                              {loadingAtletas[index] ? (
                                                <CircularProgress size={20} />
                                              ) : null}
                                              {params.InputProps.endAdornment}
                                            </>
                                          ),
                                        }}
                                      />
                                    )}
                                    disabled={saving}
                                  />
                                  <Button
                                    size="small"
                                    onClick={() => handleOpenCreateAtletaModal(index)}
                                    disabled={saving}
                                    sx={{ mt: 1 }}
                                  >
                                    Criar novo atleta
                                  </Button>

                                  {atletasSelecionados[index] ? (
                                    <Box sx={{ display: 'flex', gap: 2, mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                                      <Avatar
                                        src={atletasSelecionados[index]?.imagemUrl || '/assets/images/user.png'}
                                        alt={atletasSelecionados[index]?.nome || ''}
                                        sx={{ width: 48, height: 48, flexShrink: 0 }}
                                      />
                                      <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography variant="subtitle2" fontWeight="bold" noWrap>
                                          {atletasSelecionados[index]?.nome}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" display="block" noWrap>
                                          @{atletasSelecionados[index]?.username}
                                        </Typography>
                                        {atletasSelecionados[index]?.email && (
                                          <Typography variant="caption" color="text.secondary" display="block" noWrap>
                                            {atletasSelecionados[index]?.email}
                                          </Typography>
                                        )}
                                        {atletasSelecionados[index]?.telefone && (
                                          <Typography variant="caption" color="text.secondary" display="block" noWrap>
                                            Tel: {atletasSelecionados[index]?.telefone}
                                          </Typography>
                                        )}
                                        {(torneio?.camisetas || torneio?.sapatos) && (
                                          <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                                            {torneio?.camisetas && (
                                              <Typography variant="caption" color="text.secondary">
                                                Camiseta: {atletasSelecionados[index]?.tamanhoCamiseta || '-'}
                                              </Typography>
                                            )}
                                            {torneio?.sapatos && (
                                              <Typography variant="caption" color="text.secondary">
                                                Sapato: {atletasSelecionados[index]?.tamanhoSapato ?? '-'}
                                              </Typography>
                                            )}
                                          </Box>
                                        )}
                                      </Box>
                                    </Box>
                                  ) : (
                                    <Box sx={{ display: 'flex', gap: 2, mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                                      <Avatar sx={{ width: 48, height: 48, flexShrink: 0, bgcolor: 'grey.300' }} />
                                      <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography variant="subtitle2" color="text.disabled">
                                          Nome do atleta
                                        </Typography>
                                        <Typography variant="caption" color="text.disabled" display="block">
                                          @username
                                        </Typography>
                                      </Box>
                                    </Box>
                                  )}
                                </CardContent>
                              </Card>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}

                    {/* Campos da Inscrição */}
                    <Box mb={3}>
                      <TextField
                        select
                        label="Status *"
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                        fullWidth
                        required
                        disabled={saving}
                        sx={{ mb: 2 }}
                      >
                        {getStatusOptions(isEditMode).map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>

                      <TextField
                        label="Observação"
                        value={observacao}
                        onChange={(e) => setObservacao(e.target.value)}
                        fullWidth
                        multiline
                        rows={3}
                        disabled={saving}
                        sx={{ mb: 2 }}
                      />

                      <TextField
                        label="Data de Inscrição"
                        type="datetime-local"
                        value={dataInscricao}
                        onChange={(e) => setDataInscricao(e.target.value)}
                        fullWidth
                        disabled={saving}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Box>

                    {/* Botões de Ação */}
                    <Box display="flex" gap={2} justifyContent="flex-end">
                      <Button
                        variant="outlined"
                        onClick={() => {
                          if (resolvedCategoriaId) {
                            navigate(`/liga/tournament/${torneioId}/category/${resolvedCategoriaId}/manage?tab=1`);
                          } else {
                            navigate(`/liga/tournament/${torneioId}?tab=1`);
                          }
                        }}
                        disabled={saving}
                      >
                        Voltar
                      </Button>
                      {isEditMode && (
                        <Button
                          variant="contained"
                          color="error"
                          onClick={handleDeleteClick}
                          disabled={saving}
                        >
                          Deletar
                        </Button>
                      )}
                      <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={saving || !categoria}
                      >
                        {saving ? <CircularProgress size={20} /> : 'Salvar'}
                      </Button>
                    </Box>
                    </>)}

                    {/* === TAB 1: Pagamento (somente modo edição) === */}
                    {isEditMode && tabValue === 1 && (
                      <Box>
                        {!pagamento ? (
                          <Alert severity="info">
                            Nenhum pagamento vinculado a esta inscrição.
                          </Alert>
                        ) : (
                          <Box display="flex" flexDirection="column" gap={3}>
                            {/* Campos somente leitura em grid */}
                            <Box display="flex" flexWrap="wrap" gap={2}>
                              <Box sx={{ minWidth: 140, flex: '1 1 30%' }}>
                                <Typography variant="caption" color="text.secondary">Valor</Typography>
                                <Typography variant="body1">
                                  {pagamento.valor != null ? `R$ ${Number(pagamento.valor).toFixed(2)}` : '-'}
                                </Typography>
                              </Box>
                              <Box sx={{ minWidth: 140, flex: '1 1 30%' }}>
                                <Typography variant="caption" color="text.secondary">Valor sem Desconto</Typography>
                                <Typography variant="body1">
                                  {pagamento.valorSemDesconto != null ? `R$ ${Number(pagamento.valorSemDesconto).toFixed(2)}` : '-'}
                                </Typography>
                              </Box>
                              <Box sx={{ minWidth: 140, flex: '1 1 30%' }}>
                                <Typography variant="caption" color="text.secondary">Desconto Aplicado</Typography>
                                <Typography variant="body1">
                                  {pagamento.descontoAplicado != null ? `R$ ${Number(pagamento.descontoAplicado).toFixed(2)}` : '-'}
                                </Typography>
                              </Box>
                              <Box sx={{ minWidth: 140, flex: '1 1 30%' }}>
                                <Typography variant="caption" color="text.secondary">Cupom Aplicado</Typography>
                                <Typography variant="body1">
                                  {pagamento.cupomAplicado ? `${pagamento.cupomAplicado.codigo} (R$ ${pagamento.cupomAplicado.valor})` : 'Nenhum'}
                                </Typography>
                              </Box>
                              <Box sx={{ minWidth: 140, flex: '1 1 30%' }}>
                                <Typography variant="caption" color="text.secondary">Invoice ID</Typography>
                                <Typography variant="body1">
                                  {pagamento.invoiceId || '-'}
                                </Typography>
                              </Box>
                              <Box sx={{ minWidth: 140, flex: '1 1 30%' }}>
                                <Typography variant="caption" color="text.secondary">Recibo URL</Typography>
                                <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>
                                  {pagamento.reciboUrl || '-'}
                                </Typography>
                              </Box>
                              <Box sx={{ minWidth: 140, flex: '1 1 30%' }}>
                                <Typography variant="caption" color="text.secondary">Criado em</Typography>
                                <Typography variant="body1">
                                  {pagamento.criadoEm ? dayjs(pagamento.criadoEm).format('DD/MM/YYYY HH:mm') : '-'}
                                </Typography>
                              </Box>
                            </Box>

                            {/* Campos editáveis */}
                            <Box display="flex" gap={2}>
                              <Box flex={1}>
                                <TextField
                                  select
                                  label="Status"
                                  value={pagamentoStatus}
                                  onChange={(e) => setPagamentoStatus(e.target.value)}
                                  fullWidth
                                  size="small"
                                  disabled={savingPagamento}
                                  InputLabelProps={{ shrink: true }}
                                >
                                  <MenuItem value="PENDENTE">Pendente</MenuItem>
                                  <MenuItem value="PAGO">Pago</MenuItem>
                                  <MenuItem value="CANCELADO">Cancelado</MenuItem>
                                  <MenuItem value="ESTORNADO">Estornado</MenuItem>
                                  <MenuItem value="GRATUITO">Gratuito</MenuItem>
                                </TextField>
                              </Box>
                              <Box flex={1}>
                                <TextField
                                  label="Data Vencimento"
                                  type="datetime-local"
                                  value={pagamentoDataVencimento}
                                  onChange={(e) => setPagamentoDataVencimento(e.target.value)}
                                  fullWidth
                                  size="small"
                                  disabled={savingPagamento}
                                  InputLabelProps={{ shrink: true }}
                                />
                              </Box>
                            </Box>
                            <TextField
                              label="Motivo Cancelamento"
                              value={pagamentoMotivoCancelamento}
                              onChange={(e) => setPagamentoMotivoCancelamento(e.target.value)}
                              fullWidth
                              size="small"
                              multiline
                              rows={3}
                              disabled={savingPagamento}
                              InputLabelProps={{ shrink: true }}
                            />

                            <Box display="flex" justifyContent="flex-end" gap={2} mt={1}>
                              {pagamento?.pedidosReembolso && pagamento.pedidosReembolso.length > 0 && (
                                <Button
                                  variant="outlined"
                                  onClick={() => navigate(`/athlete/payments/${pagamento.id}/refunds`)}
                                >
                                  Visualizar Reembolso
                                </Button>
                              )}
                              <Button
                                variant="contained"
                                onClick={handleSavePagamento}
                                disabled={savingPagamento}
                              >
                                {savingPagamento ? <CircularProgress size={20} /> : 'Salvar Pagamento'}
                              </Button>
                            </Box>
                          </Box>
                        )}
                      </Box>
                    )}
                  </div>
                </Paper>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal para criar novo atleta */}
      <Dialog
        open={showCreateAtletaModal}
        onClose={() => setShowCreateAtletaModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Criar Novo Atleta</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 1 }}>
            <TextField
              label="Nome *"
              value={novoAtletaNome}
              onChange={(e) => setNovoAtletaNome(e.target.value)}
              fullWidth
              required
              disabled={creatingAtleta}
            />
            <TextField
              label="Email *"
              type="email"
              value={novoAtletaEmail}
              onChange={(e) => setNovoAtletaEmail(e.target.value)}
              fullWidth
              required
              disabled={creatingAtleta}
            />
            <TextField
              label="CPF"
              value={novoAtletaCpf}
              onChange={(e) => setNovoAtletaCpf(e.target.value)}
              fullWidth
              disabled={creatingAtleta}
            />
            <TextField
              label="Telefone"
              value={novoAtletaTelefone}
              onChange={(e) => setNovoAtletaTelefone(e.target.value)}
              fullWidth
              disabled={creatingAtleta}
            />
            <TextField
              label="Data de Nascimento"
              type="date"
              value={novoAtletaDataNascimento}
              onChange={(e) => setNovoAtletaDataNascimento(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              disabled={creatingAtleta}
            />
            <TextField
              label="Cidade ID"
              value={novoAtletaCidadeId}
              onChange={(e) => setNovoAtletaCidadeId(e.target.value)}
              fullWidth
              disabled={creatingAtleta}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreateAtletaModal(false)} disabled={creatingAtleta}>
            Cancelar
          </Button>
          <Button
            onClick={handleCreateAtleta}
            variant="contained"
            disabled={creatingAtleta || !novoAtletaNome.trim() || !novoAtletaEmail.trim()}
          >
            {creatingAtleta ? <CircularProgress size={20} /> : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de confirmação de delete */}
      <Dialog open={deleteDialogOpen} onClose={saving ? undefined : () => setDeleteDialogOpen(false)}>
        <DialogTitle>Deletar Inscrição</DialogTitle>
        <DialogContent dividers>
          <Typography>
            Tem certeza que deseja deletar esta inscrição e pagamento?
          </Typography>
          <Typography>
            Esses dados serão perdidos para sempre. Considere cancelar ao invés de deletar.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={saving}
          >
            {saving ? 'Deletando...' : 'Deletar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de bloqueio de delete (invoice existente) */}
      <Dialog open={deleteBlockedDialogOpen} onClose={() => setDeleteBlockedDialogOpen(false)}>
        <DialogTitle>Não é possível deletar</DialogTitle>
        <DialogContent dividers>
          <Typography>
            Este pagamento não pode ser deletado pois tem uma invoice criada para ele. Deletar pode criar inconsistência de dados. Considere somente cancelar.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteBlockedDialogOpen(false)} variant="contained">
            Entendi
          </Button>
        </DialogActions>
      </Dialog>

      <Appfooter />

      {/* Snackbar para mensagens de sucesso/erro */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default EditInscricaoPage;

